import type { Atom } from "#public"
import type { Sequence } from "#public"

import { Identity } from "./Identity"

/**
 * Removes duplicates from a sequence, yielding unique items as they appear.
 *
 * Uses an atomic key function to determine uniqueness. Keeps the first
 * occurrence. Generator function for lazy evaluation.
 *
 * @param sequence - Sequence to deduplicate
 * @param f - Key function returning an Atom (default: Identity)
 * @returns Generator yielding items with duplicates removed
 *
 * @example
 * ```ts
 * import { DeleteDuplicates } from "@pdfking/tfunc"
 *
 * Array.from(DeleteDuplicates([1, 2, 1, 3])) // [1, 2, 3]
 * Array.from(DeleteDuplicates([{ a: 1 }, { a: 1 }, { a: 2 }], v => v.a)) // [{ a: 1 }, { a: 2 }]
 * ```
 */
export const DeleteDuplicates = function* <T>(
  sequence: Sequence<T>,
  f: (item: T) => Atom = Identity as (item: T) => Atom
): Generator<T> {
  const seen = new Set<Atom>()
  for (const item of sequence) {
    const key = f(item)
    if (seen.has(key)) continue
    seen.add(key)
    yield item
  }
}
